import React, { useEffect, useState, useRef } from "react";
import { twMerge } from "tailwind-merge";
import classNames from "classnames";
import { Link } from "@app/components/common";
import { useGenericData } from "@app/hooks/use-generic-data/useGenericData";
import { Logo } from "../icons";
import { NotificationBar } from "./components/notification-bar/NotificationBar";
import { useNavigationCollapse } from "./useNavigationCollapse";
import { useAssistantsVisibilityToggler } from "./useAssistantsVisibilityToggler";
import { Controls } from "./components/controls/Controls";
import { MobileNavigation } from "./components/mobile-navigation/MobileNavigation";
import { FullNavigation } from "./components/full-navigation/FullNavigation";
import { FullNavigationDropdownMenu } from "./components/full-navigation-dropdown-menu/FullNavigationDropdownMenu";

const SCROLL_THRESHOLD = 40;

export const Header = () => {
  const { headerNavigation, notificationBar } = useGenericData();
  const isNotificationBarEnabled = notificationBar?.isEnabled;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTopShift, setIsTopShift] = useState(false);
  const [activeMenuGroup, setActiveMenuGroup] = useState(null);
  const dropdownRef = useRef(null);
  const {
    navRef,
    lastItemRef,
    buttonRef,
    isCollapsed: isNavCollapsed,
  } = useNavigationCollapse();

  const handleScroll = () => {
    if (isNotificationBarEnabled) {
      setIsTopShift(window.scrollY > SCROLL_THRESHOLD);
    }
  };

  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (isNotificationBarEnabled) {
      setIsTopShift(window.scrollY > SCROLL_THRESHOLD);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const container = navRef.current;
    const dropdown = dropdownRef.current;

    if (!container || !dropdown) return;

    let timeout: NodeJS.Timeout;

    const handleMouseLeave = () => {
      timeout = setTimeout(() => {
        setActiveMenuGroup(null);
      }, 100);
    };

    const handleMouseEnter = () => {
      clearTimeout(timeout);
    };

    container.addEventListener("mouseleave", handleMouseLeave);
    container.addEventListener("mouseenter", handleMouseEnter);
    dropdown.addEventListener("mouseleave", handleMouseLeave);
    dropdown.addEventListener("mouseenter", handleMouseEnter);

    return () => {
      container.removeEventListener("mouseleave", handleMouseLeave);
      container.removeEventListener("mouseenter", handleMouseEnter);
      dropdown.removeEventListener("mouseleave", handleMouseLeave);
      dropdown.removeEventListener("mouseenter", handleMouseEnter);
    };
  }, [activeMenuGroup]);

  useAssistantsVisibilityToggler(isMenuOpen);

  return (
    <header>
      {isNotificationBarEnabled ? (
        <NotificationBar {...notificationBar} />
      ) : null}
      <div
        className={twMerge(
          classNames(
            "bg-primary-content/10 fixed top-4 right-4 left-4 z-[900] flex items-center justify-between rounded-2xl px-5 backdrop-blur-md duration-300",
            "lg:top-7.5 lg:right-7.5 lg:left-7.5 lg:px-10",
            "xxxl:max-w-[2500px] xxxl:mx-auto",
            {
              "translate-y-10": isNotificationBarEnabled && !isTopShift,
            },
          ),
        )}
      >
        <div className="flex items-center gap-x-9">
          <Link
            to="/"
            className="shrink-0"
            onClick={() => setIsMenuOpen(false)}
          >
            <Logo className="fill-primary-content w-24" />
          </Link>
          <FullNavigation
            {...{
              data: headerNavigation,
              isCollapsed: isNavCollapsed,
              navRef,
              lastItemRef,
              setActiveMenuGroup,
            }}
          />
        </div>

        <Controls
          {...{
            isNavCollapsed,
            buttonRef,
            isMenuOpen,
            onToggleMenuState: setIsMenuOpen,
          }}
        />
      </div>
      {isNavCollapsed && isMenuOpen && (
        <MobileNavigation
          data={headerNavigation}
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          {...{ isTopShift }}
        />
      )}
      {!isNavCollapsed && (
        <FullNavigationDropdownMenu
          {...{
            data: headerNavigation,
            activeMenuGroup,
            dropdownRef,
            setActiveMenuGroup,
            isNotificationBarEnabled,
            isTopShift,
          }}
        />
      )}
    </header>
  );
};
